
import React, { useMemo } from 'react';
import { Product, ProductVariant } from '../types';
import { bundleService } from '../services/bundleService';
import { Plus, Equal, ShoppingCart, Sparkles, Zap, ArrowRight } from 'lucide-react';
import { useCurrency } from '../contexts/CurrencyContext';

interface Props {
  product: Product;
  onAddToCart: (product: Product) => void;
}

const ProductBundles: React.FC<Props> = ({ product, onAddToCart }) => {
  const suggestion = useMemo(() => bundleService.getSmartSuggestion(product), [product]);
  const { format } = useCurrency();

  if (!suggestion) return null;

  const allItems = [product, ...suggestion.products];
  const originalTotal = allItems.reduce((sum, p) => sum + p.price, 0);
  const bundleTotal = originalTotal * (1 - suggestion.discount / 100);

  const handleAddBundle = () => {
    allItems.forEach(p => onAddToCart(p));
  };

  return (
    <div className="mt-20 p-10 bg-white border border-blue-100 rounded-[4rem] shadow-2xl shadow-blue-50 relative overflow-hidden group">
       <div className="absolute top-0 right-0 p-8 opacity-5 group-hover:scale-110 transition-transform duration-1000"><Zap className="h-40 w-40 text-blue-600" /></div>
       
       <div className="relative space-y-10">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
             <div className="space-y-1 text-center md:text-left">
                <div className="flex items-center justify-center md:justify-start gap-2 mb-2">
                   <span className="bg-blue-600 text-white text-[8px] font-black px-3 py-1 rounded-full uppercase tracking-widest flex items-center gap-1 shadow-lg">
                      <Sparkles className="h-3 w-3" /> {suggestion.isSmart ? 'Recommended Combo' : 'Power Set Bundle'}
                   </span>
                </div>
                <h3 className="text-3xl font-black text-gray-900 tracking-tight leading-none">Frequently Bought Together</h3>
                <p className="text-gray-400 font-medium">Bhai, ye accessories aapke tech setup ko complete karenge.</p>
             </div>
             <div className="text-center md:text-right bg-emerald-50 px-6 py-3 rounded-2xl border border-emerald-100">
                <p className="text-[10px] font-black text-emerald-600 uppercase tracking-widest">Savings Unlocked</p>
                <p className="text-2xl font-black text-emerald-700">Save {suggestion.discount}%</p>
             </div>
          </div>

          <div className="flex flex-col lg:flex-row items-center justify-center gap-6 md:gap-10">
             {allItems.map((item, idx) => (
               <React.Fragment key={item.id}>
                  <div className="flex flex-col items-center gap-4 group/item">
                     <div className="w-32 h-32 rounded-3xl bg-gray-50 border border-gray-100 overflow-hidden shadow-md group-hover/item:scale-105 transition-all">
                        <img src={item.images[0]} className="w-full h-full object-cover" alt="" />
                     </div>
                     <div className="text-center">
                        <p className="text-xs font-black text-gray-900 truncate w-32">{item.name}</p>
                        <p className="text-[10px] font-bold text-blue-600">{format(item.price)}</p>
                     </div>
                  </div>
                  {idx < allItems.length - 1 && (
                    <div className="text-gray-200"><Plus className="h-6 w-6" /></div>
                  )}
               </React.Fragment>
             ))}
             
             <div className="hidden lg:block text-gray-200"><Equal className="h-6 w-6" /></div>

             <div className="bg-gray-50 p-8 rounded-[2.5rem] border border-gray-100 min-w-[240px] text-center space-y-4 shadow-inner">
                <div>
                   <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest line-through mb-1">{format(originalTotal)}</p>
                   <p className="text-4xl font-black text-gray-900 tracking-tighter">{format(bundleTotal)}</p>
                </div>
                <button 
                  onClick={handleAddBundle}
                  className="w-full py-5 bg-gray-900 text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-gray-200 hover:bg-blue-600 hover:scale-105 active:scale-95 transition-all flex items-center justify-center gap-2"
                >
                   Add Entire Bundle <ShoppingCart className="h-4 w-4" />
                </button>
             </div>
          </div>
       </div>
    </div>
  );
};

export default ProductBundles;
